import { useAuth, useUser } from '@clerk/clerk-react';
import posthog from 'posthog-js';
import { PostHogProvider as PostHogReactProvider, usePostHog } from 'posthog-js/react';
import { type ReactNode, useEffect, useLayoutEffect } from 'react';
import { useCookieConsentStore } from '@/stores/use-cookie-consent-store';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
// Same-origin reverse proxy — `/ingest` is rewritten to PostHog ingestion in
// vite.config.ts (dev) and vercel.json (prod). Recovers ad-blocked events.
const POSTHOG_API_HOST = '/ingest';
// UI host points at the dashboard so links from session replay / events open the right region.
const POSTHOG_UI_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.posthog.com';

/**
 * PostHog provider for the panel app.
 *
 * Configuration goals (see docs/analytics/POSTHOG.md):
 *  - Stay strictly under the 1M-event/mo free tier at ~10K MAU
 *  - Capture every business-state change from the server (Convex triggers)
 *  - Capture explicit business intent from the client
 *  - Replace blind autocapture with an opt-in `[data-ph]` allowlist
 *  - Session replay sampled tightly: 1% normal + 100% on errors
 *
 * When `VITE_POSTHOG_KEY` is unset (dev without analytics), this becomes a
 * pass-through provider — no init, no network, no errors.
 *
 * The SDK stays uninitialized until this browser allows analytics. Init runs in
 * a layout effect so it finishes before child effects identify the user, and
 * the provider stays mounted so accepting cookies does not remount the app.
 * `opt_in_capturing()` sends `$opt_in`, so it runs only when this browser is
 * not already opted in.
 */
const POSTHOG_OPTIONS = {
    api_host: POSTHOG_API_HOST,
    ui_host: POSTHOG_UI_HOST,
    // Pin SDK behavior so future releases can't silently change pageview / autocapture semantics.
    defaults: '2026-01-30',

    // Anonymous browsers don't create person profiles — saves quota and matches privacy expectations.
    person_profiles: 'identified_only',

    // Opt-in autocapture only: clicks on elements tagged `data-ph` get captured automatically.
    // Free-tier survival requires this — blanket autocapture would 5–10× our event volume.
    autocapture: {
        css_selector_allowlist: ['[data-ph]'],
    },

    // Capture one pageview per SPA route change (TanStack Router navigates via the
    // History API). Set explicitly rather than inheriting from `defaults` — pageviews
    // are the single largest slice of event volume, so it's worth being deliberate.
    capture_pageview: 'history_change',
    // Pageleave doubles pageview cost for marginal insight.
    capture_pageleave: false,

    // Unhandled JS errors flow to Error Tracking (separate quota from Product Analytics).
    capture_exceptions: true,

    // Session replay OFF by default — this is the single easiest way to blow the
    // 5K-recording/mo free quota (at ~10K MAU, recording every session is ~16× over).
    // posthog-js has no client-side `sample_rate`; sampling lives in the PostHog
    // dashboard (Project Settings → Session Replay). To turn replay on safely:
    //   1. Set a sample rate (e.g. 5%) + minimum duration (5s) in the dashboard
    //   2. Flip this to `false`
    // Then PostHog samples server-side and we stay under quota. Until then, no replays.
    disable_session_recording: true,

    // Privacy-conscious default: don't capture form field values via autocapture.
    mask_all_text: false,
    mask_personal_data_properties: true,

    // Nothing is stored or sent until `opt_in_capturing()` runs after Accept.
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
} satisfies Parameters<typeof posthog.init>[1];

function enableAnalytics(apiKey: string) {
    if (!posthog.__loaded) {
        posthog.init(apiKey, POSTHOG_OPTIONS);
    }
    if (!posthog.has_opted_in_capturing()) {
        posthog.opt_in_capturing();
    }
}

export function PostHogProvider({ children }: { children: ReactNode }) {
    const analyticsAllowed = useCookieConsentStore((state) => state.analytics === true);

    useLayoutEffect(() => {
        if (!POSTHOG_KEY || !analyticsAllowed) return;
        enableAnalytics(POSTHOG_KEY);
    }, [analyticsAllowed]);

    if (!POSTHOG_KEY) {
        return <>{children}</>;
    }

    return (
        <PostHogReactProvider client={posthog}>
            {analyticsAllowed ? <PostHogClerkBridge /> : null}
            {children}
        </PostHogReactProvider>
    );
}

/**
 * Bridges Clerk auth state to PostHog identity.
 *
 * - On sign-in: identify with Clerk userId (matches `ctx.auth.getUserIdentity().subject`
 *   server-side, so events from Convex merge into the same person).
 * - On sign-out: reset to a fresh anonymous distinct_id.
 * - Guarded by `_isIdentified()` so it only fires on state transitions, not every render.
 */
function PostHogClerkBridge() {
    const ph = usePostHog();
    const { isLoaded, isSignedIn, userId } = useAuth();
    const { user } = useUser();

    useEffect(() => {
        if (!ph || !isLoaded) return;

        if (isSignedIn && userId && user) {
            // Type the private method without polluting global types.
            const alreadyIdentified = (ph as unknown as { _isIdentified: () => boolean })._isIdentified();
            if (!alreadyIdentified) {
                ph.identify(userId, {
                    // Person-property contract is documented in convex/_infra/analyticsEvents.ts
                    // (AnalyticsPersonProperties). Keep this list in sync.
                    clerk_user_id: userId,
                    email_domain: user.primaryEmailAddress?.emailAddress.split('@')[1],
                    username: user.username ?? undefined,
                    created_at: user.createdAt?.getTime(),
                });
            }
            return;
        }

        if (!isSignedIn) {
            const alreadyIdentified = (ph as unknown as { _isIdentified: () => boolean })._isIdentified();
            if (alreadyIdentified) {
                // `reset()` clears consent. With opt-out-by-default that would stop
                // capturing even though this browser already allowed analytics.
                ph.reset();
                ph.opt_in_capturing();
            }
        }
    }, [ph, isLoaded, isSignedIn, userId, user]);

    return null;
}

/**
 * Re-export `posthog-js` singleton for places that need imperative access
 * (e.g. error boundaries, click handlers far from React context).
 */
export { posthog };
